import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { X as XIcon, ChevronRight as ChevronRightIcon } from "lucide-react";

const STORAGE_KEY = "cutmate:onboarding:v1";

interface Step {
  title: string;
  body: string;
  anchor?: string; // CSS selector; highlight this element if found
}

/**
 * Lightweight onboarding tour (M-10).
 *
 * Shows a 3-step bottom sheet on the first ever visit to the editor.
 * Each step has a title + body + Next button. Dismiss via Skip or X
 * writes to localStorage so the tour doesn't reappear.
 *
 * Deliberately NOT a blocking overlay — the user can still click
 * around; the tour just sits at the bottom of the screen explaining
 * what they see.
 */
export function OnboardingTour() {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [step, setStep] = useState(0);

  useEffect(() => {
    try {
      if (!window.localStorage.getItem(STORAGE_KEY)) {
        // Delay a tick so the editor content renders first
        const handle = window.setTimeout(() => setOpen(true), 600);
        return () => window.clearTimeout(handle);
      }
    } catch {
      // Private browsing — don't show the tour at all rather than
      // looping on every page load.
    }
  }, []);

  const dismiss = () => {
    setOpen(false);
    try {
      window.localStorage.setItem(STORAGE_KEY, String(Date.now()));
    } catch {
      // ignore
    }
  };

  const steps: Step[] = [
    {
      title: t("tour.s1Title"),
      body: t("tour.s1Body"),
    },
    {
      title: t("tour.s2Title"),
      body: t("tour.s2Body"),
    },
    {
      title: t("tour.s3Title"),
      body: t("tour.s3Body"),
    },
  ];

  if (!open) return null;
  const current = steps[step]!;
  const isLast = step === steps.length - 1;

  return (
    <div
      role="dialog"
      aria-label={t("tour.ariaLabel")}
      aria-modal="false"
      className="fixed inset-x-4 bottom-20 z-40 rounded-lg border border-slate-200 bg-white p-4 shadow-xl dark:border-slate-700 dark:bg-slate-800 sm:inset-x-auto sm:bottom-6 sm:end-6 sm:max-w-sm"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium uppercase tracking-wide text-brand-700 dark:text-brand-100">
            {t("tour.stepLabel", { current: step + 1, total: steps.length })}
          </p>
          <h3 className="mt-1 text-sm font-semibold text-slate-900 dark:text-slate-100">
            {current.title}
          </h3>
          <p className="mt-1 text-sm text-slate-600 dark:text-slate-300">
            {current.body}
          </p>
        </div>
        <button
          type="button"
          onClick={dismiss}
          aria-label={t("tour.skip")}
          className="flex h-7 w-7 shrink-0 items-center justify-center rounded text-slate-500 hover:bg-slate-100 dark:hover:bg-slate-700"
        >
          <XIcon className="h-4 w-4" />
        </button>
      </div>

      <div className="mt-4 flex items-center justify-between">
        <button
          type="button"
          onClick={dismiss}
          className="text-xs text-slate-500 underline underline-offset-2 hover:text-slate-700 dark:hover:text-slate-300"
        >
          {t("tour.skip")}
        </button>
        <button
          type="button"
          onClick={() => (isLast ? dismiss() : setStep((n) => n + 1))}
          className="btn-primary flex items-center gap-1 px-3 py-1.5 text-xs"
        >
          {isLast ? t("tour.done") : t("tour.next")}
          {!isLast && <ChevronRightIcon className="h-3.5 w-3.5" />}
        </button>
      </div>
    </div>
  );
}
